import { useState, useEffect } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  FileText,
  CheckCircle2,
  Clock,
  XCircle,
  Download,
  File,
  FileSpreadsheet,
  Image,
  AlertCircle,
  Loader2,
  Package,
  Receipt,
  ShoppingCart,
  TrendingUp,
  CircleDashed,
  Ban,
  FolderOpen,
  History,
  Info,
  Building2,
} from 'lucide-react';
import { format } from 'date-fns';
import type { PurchaseRequest, PurchaseRequestStatus, PurchaseRequestLine, Document } from '@/types/purchase-request';
import type { FinOpsPurchaseRequestDetail } from '@/types/finops.types';
import { ApprovalHistoryTimeline } from './approval-history-timeline';

// ─────────────────────────────────────────────────────────────────────────────
// Props
// ─────────────────────────────────────────────────────────────────────────────

interface PurchaseRequestFullModalProps {
  purchaseRequest: PurchaseRequest | null;
  allPurchaseRequests: PurchaseRequest[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPurchaseRequestChange: (pr: PurchaseRequest) => void;
  projectCode?: string;
  instanceId?: string;
  onFetchLineDetails?: (projectCode: string, instanceId: string) => Promise<FinOpsPurchaseRequestDetail[] | null>;
  resolveEmployeeName?: (externalUserId: string) => string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function mapFinOpsDetailToLine(detail: FinOpsPurchaseRequestDetail): PurchaseRequestLine {
  return {
    id: `finops-line-${detail.primaryKey}`,
    lineNumber: detail.lineNumber,
    lineType: detail.lineType || detail.type || 'Price',
    description: detail.description || '',
    quantity: detail.quantity || 0,
    unitPrice: detail.unitPrice,
    totalAmount: detail.total || detail.value || detail.gross || 0,
    currency: detail.currencyCode || detail.currSymbol || 'GBP',
    costCentreCode: detail.costCentreCode || '',
    costCentreName: detail.costCentreName,
    unitOfMeasure: detail.unitOfMeasure,
    taxCode: detail.taxCode || detail.vatCode,
    taxAmount: detail.taxAmount || detail.vat,
    projectTaskCode: detail.projectTask,
    glAccount: detail.glAccount,
    notes: detail.notes,
  };
}

const statusConfig: Record<string, {
  variant: 'success' | 'warning' | 'destructive' | 'default';
  accent: 'success' | 'warning' | 'destructive' | 'info';
  icon: typeof CheckCircle2;
  color: string;
}> = {
  Approved:            { variant: 'success',     accent: 'success',     icon: CheckCircle2, color: 'text-success-600' },
  Pending:             { variant: 'warning',     accent: 'warning',     icon: Clock,        color: 'text-orange-600' },
  'Awaiting Approval': { variant: 'warning',     accent: 'warning',     icon: Clock,        color: 'text-orange-600' },
  Rejected:            { variant: 'destructive', accent: 'destructive', icon: XCircle,      color: 'text-error-600' },
  Posted:              { variant: 'default',     accent: 'info',        icon: CheckCircle2, color: 'text-blue-600' },
  Redirected:          { variant: 'warning',     accent: 'warning',     icon: AlertCircle,  color: 'text-orange-600' },
  Reopened:            { variant: 'warning',     accent: 'warning',     icon: AlertCircle,  color: 'text-warning-600' },
  Cancelled:           { variant: 'destructive', accent: 'destructive', icon: XCircle,      color: 'text-neutral-600' },
  Draft:               { variant: 'default',     accent: 'info',        icon: FileText,     color: 'text-neutral-600' },
};

const getStatusConfig = (status: string) =>
  statusConfig[status] || {
    variant: 'default' as const,
    accent:  'info'    as const,
    icon:    AlertCircle,
    color:   'text-neutral-600',
  };

const getDocumentIcon = (type: Document['type']) => {
  switch (type) {
    case 'pdf':         return FileText;
    case 'image':       return Image;
    case 'spreadsheet': return FileSpreadsheet;
    case 'document':    return FileText;
    default:            return File;
  }
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

const formatCurrency = (amount: number, currency = 'GBP') => {
  try {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency,
      currencyDisplay: 'symbol',
    }).format(amount) + ` ${currency}`;
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

/** Two-column label / value pair used throughout the Summary tab */
function Field({ label, value, colSpan = 1 }: { label: string; value: React.ReactNode; colSpan?: number }) {
  return (
    <div className={colSpan === 2 ? 'col-span-2' : ''}>
      <p className="text-xs text-muted-foreground mb-0.5">{label}</p>
      <p className="text-sm font-medium">{value ?? <span className="text-muted-foreground">—</span>}</p>
    </div>
  );
}

/** A slim section heading used to separate logical groups */
function SectionHeading({ icon: Icon, title }: { icon: typeof FileText; title: string }) {
  return (
    <div className="flex items-center gap-2 pt-4 pb-1">
      <Icon className="h-3.5 w-3.5 text-muted-foreground" />
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</p>
    </div>
  );
}

/** Financial journey step — shows a stage with its icon, label and status pill */
function JourneyStep({
  icon: Icon,
  label,
  status,
  statusVariant,
  children,
  isLast = false,
}: {
  icon: typeof FileText;
  label: string;
  status: string;
  statusVariant: 'success' | 'warning' | 'destructive' | 'default' | 'secondary' | 'outline';
  children?: React.ReactNode;
  isLast?: boolean;
}) {
  return (
    <div className="flex gap-3">
      {/* Spine */}
      <div className="flex flex-col items-center">
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted border">
          <Icon className="h-4 w-4 text-muted-foreground" />
        </div>
        {!isLast && <div className="w-px flex-1 bg-border mt-1 mb-1 min-h-[16px]" />}
      </div>

      {/* Content */}
      <div className={`flex-1 pb-4 ${isLast ? '' : ''}`}>
        <div className="flex items-center gap-2 mb-1">
          <p className="text-sm font-medium">{label}</p>
          <Badge variant={statusVariant} className="text-xs">{status}</Badge>
        </div>
        {children && (
          <div className="text-sm text-muted-foreground space-y-0.5">{children}</div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────

export function PurchaseRequestFullModal({
  purchaseRequest,
  allPurchaseRequests,
  open,
  onOpenChange,
  onPurchaseRequestChange,
  projectCode,
  instanceId,
  onFetchLineDetails,
  resolveEmployeeName,
}: PurchaseRequestFullModalProps) {
  const [activeTab, setActiveTab] = useState('summary');
  const [lineItems, setLineItems] = useState<PurchaseRequestLine[]>([]);
  const [lineItemsLoading, setLineItemsLoading] = useState(false);
  const [lineItemsError, setLineItemsError] = useState<string | null>(null);
  const [lineItemsFetched, setLineItemsFetched] = useState(false);

  // Reset on open / PR change
  useEffect(() => {
    if (open) {
      setActiveTab('summary');
      setLineItems([]);
      setLineItemsError(null);
      setLineItemsFetched(false);
    }
  }, [open, purchaseRequest?.id]);

  // Fetch line items when Lines tab selected
  useEffect(() => {
    const fetch = async () => {
      if (activeTab !== 'lines' || lineItemsFetched || !purchaseRequest) return;

      if (purchaseRequest.lines && purchaseRequest.lines.length > 0) {
        setLineItems(purchaseRequest.lines);
        setLineItemsFetched(true);
        return;
      }

      if (onFetchLineDetails && projectCode && instanceId) {
        setLineItemsLoading(true);
        setLineItemsError(null);
        try {
          const details = await onFetchLineDetails(projectCode, instanceId);
          if (details && details.length > 0) {
            const headerKey = purchaseRequest.id.startsWith('finops-')
              ? parseInt(purchaseRequest.id.replace('finops-', ''), 10)
              : null;
            const filtered = headerKey !== null
              ? details.filter(d => d.headerKey === headerKey)
              : details;
            setLineItems(filtered.map(mapFinOpsDetailToLine));
          } else {
            setLineItems([]);
          }
        } catch (err) {
          console.error('[PurchaseRequestFullModal] Error fetching line details:', err);
          setLineItemsError('Failed to load line items');
        } finally {
          setLineItemsLoading(false);
          setLineItemsFetched(true);
        }
      } else {
        if (!instanceId) {
          console.warn('[PurchaseRequestFullModal] instanceId is undefined – cannot fetch line items');
          setLineItemsError('Line item details require a FinOps integration configuration. Please verify the company has a valid FinOps instance configured.');
        }
        setLineItemsFetched(true);
      }
    };
    fetch();
  }, [activeTab, lineItemsFetched, purchaseRequest, projectCode, instanceId, onFetchLineDetails]);

  // Navigation
  const currentIndex = purchaseRequest
    ? allPurchaseRequests.findIndex(pr => pr.id === purchaseRequest.id)
    : -1;
  const canPrev = currentIndex > 0;
  const canNext = currentIndex < allPurchaseRequests.length - 1;

  const handlePrev = () => { if (canPrev) onPurchaseRequestChange(allPurchaseRequests[currentIndex - 1]); };
  const handleNext = () => { if (canNext) onPurchaseRequestChange(allPurchaseRequests[currentIndex + 1]); };

  // Keyboard navigation
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!open || !purchaseRequest) return;
      if (e.key === 'ArrowLeft' && canPrev) handlePrev();
      else if (e.key === 'ArrowRight' && canNext) handleNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, purchaseRequest, canPrev, canNext]);

  if (!purchaseRequest) return null;

  const cfg = getStatusConfig(purchaseRequest.status);
  const StatusIcon = cfg.icon;

  // ── Derived financial journey values ──────────────────────────────────────

  // PR stage
  const prStatus = purchaseRequest.status;
  const isInFlight = ['Pending', 'Awaiting Approval', 'Reopened', 'Redirected'].includes(prStatus);
  const isPosted = prStatus === 'Posted';
  const isApproved = prStatus === 'Approved';
  const isRejected = prStatus === 'Rejected' || prStatus === 'Cancelled';

  // PO / Order step
  const hasOrder = isPosted || (purchaseRequest.poNumber && purchaseRequest.poNumber.length > 0);
  const poNumbers = purchaseRequest.poNumber ?? [];
  const poStatus = purchaseRequest.poStatus;

  // GR step — derived from PO detail line quantities (proxy — no dedicated GR endpoint)
  // Engineering: replace with `goodsReceivedStatus` from Financials when available
  const grStatus = purchaseRequest.goodsReceivedStatus;
  const grValue = purchaseRequest.goodsReceivedValue;

  // Invoice step
  const invoiceStatus = purchaseRequest.invoiceStatus;
  const invoiceMatchedStatus = purchaseRequest.invoiceMatchedStatus;
  const invoiceNumbers = purchaseRequest.invoiceNumbers ?? [];

  // Commitment relief
  const isRelieved = purchaseRequest.isCommitmentRelieved === true;

  // Tax
  const taxAmount = purchaseRequest.taxAmount ?? 0;
  const grossAmount = purchaseRequest.amount + taxAmount;

  // Resolve name helper
  const resolveName = (id: string) =>
    resolveEmployeeName ? resolveEmployeeName(id) : id;

  // Tab badge counts
  const docCount = purchaseRequest.documents?.length ?? 0;
  const historyCount = purchaseRequest.approvalHistory?.length ?? 0;

  // Line totals
  const lineNet   = lineItems.reduce((s, l) => s + (l.lineType !== 'Text' ? l.totalAmount : 0), 0);
  const lineTax   = lineItems.reduce((s, l) => s + (l.taxAmount ?? 0), 0);
  const lineGross = lineNet + lineTax;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-[900px] p-0 flex flex-col"
      >
        {/* ── Header ── */}
        <SheetHeader className="px-6 pt-5 pb-4 border-b shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <ShoppingCart className="h-4 w-4 text-muted-foreground shrink-0" />
                <SheetTitle className="text-base">{purchaseRequest.requestId}</SheetTitle>
                <Badge variant={cfg.variant} accent={cfg.accent} className="gap-1 shrink-0">
                  <StatusIcon className="h-3 w-3" />
                  {purchaseRequest.status}
                </Badge>
              </div>
              <SheetDescription className="truncate">{purchaseRequest.description}</SheetDescription>
            </div>

            {/* Navigation */}
            <div className="flex items-center gap-1 shrink-0">
              <span className="text-xs text-muted-foreground tabular-nums">
                {currentIndex + 1}/{allPurchaseRequests.length}
              </span>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handlePrev} disabled={!canPrev} title="Previous (←)">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleNext} disabled={!canNext} title="Next (→)">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </SheetHeader>

        {/* ── Tabs ── */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="mx-6 mt-3 shrink-0 justify-start">
            <TabsTrigger value="summary">Summary</TabsTrigger>
            <TabsTrigger value="journey">Financial Journey</TabsTrigger>
            <TabsTrigger value="lines">Line Items</TabsTrigger>
            <TabsTrigger value="documents">
              Documents{docCount > 0 ? ` (${docCount})` : ''}
            </TabsTrigger>
            <TabsTrigger value="history">
              History{historyCount > 0 ? ` (${historyCount})` : ''}
            </TabsTrigger>
          </TabsList>

          {/* ═══════════════════════════════════════════════════════════════
              TAB 1 — SUMMARY
          ══════════════════════════════════════════════════════════════════ */}
          <TabsContent value="summary" className="flex-1 overflow-y-auto px-6 pb-6 mt-4 space-y-1">

            {/* Financial snapshot strip */}
            <div className="grid grid-cols-3 gap-px bg-border rounded-lg overflow-hidden mb-4">
              {[
                { label: 'Net Amount', value: formatCurrency(purchaseRequest.amount, purchaseRequest.currency) },
                { label: 'Tax',        value: taxAmount > 0 ? formatCurrency(taxAmount, purchaseRequest.currency) : '—' },
                { label: 'Gross Total',value: taxAmount > 0 ? formatCurrency(grossAmount, purchaseRequest.currency) : formatCurrency(purchaseRequest.amount, purchaseRequest.currency) },
              ].map(({ label, value }) => (
                <div key={label} className="bg-muted/40 px-4 py-3 text-center">
                  <p className="text-xs text-muted-foreground mb-0.5">{label}</p>
                  <p className="text-sm font-semibold">{value}</p>
                </div>
              ))}
            </div>

            {/* PR Header */}
            <SectionHeading icon={FileText} title="Request" />
            <div className="grid grid-cols-2 gap-x-6 gap-y-3">
              <Field label="Request ID"   value={purchaseRequest.requestId} />
              <Field label="Date Raised"  value={format(new Date(purchaseRequest.date), 'dd MMM yyyy')} />
              <Field label="Category"     value={purchaseRequest.category} />
              <Field label="Currency"     value={purchaseRequest.currency} />
              {purchaseRequest.poNumber && purchaseRequest.poNumber.length > 0 && (
                <Field label="Order / PO Number" value={purchaseRequest.poNumber.join(', ')} />
              )}
              {purchaseRequest.costCentreCode && (
                <Field
                  label="Cost Centre"
                  value={`${purchaseRequest.costCentreCode}${purchaseRequest.costCentreName ? ` — ${purchaseRequest.costCentreName}` : ''}`}
                />
              )}
              {purchaseRequest.notes && (
                <div className="col-span-2">
                  <p className="text-xs text-muted-foreground mb-0.5">Notes</p>
                  <p className="text-sm bg-muted/30 rounded-md px-3 py-2">{purchaseRequest.notes}</p>
                </div>
              )}
            </div>

            <Separator className="mt-4" />

            {/* Supplier */}
            <SectionHeading icon={Building2} title="Supplier" />
            <div className="grid grid-cols-2 gap-x-6 gap-y-3">
              <Field label="Supplier"     value={purchaseRequest.supplierName || resolveName(purchaseRequest.supplier)} />
              {purchaseRequest.supplierContactName  && <Field label="Contact"      value={purchaseRequest.supplierContactName} />}
              {purchaseRequest.supplierContactEmail && <Field label="Email"        value={purchaseRequest.supplierContactEmail} />}
              {purchaseRequest.supplierContactPhone && <Field label="Phone"        value={purchaseRequest.supplierContactPhone} />}
              {purchaseRequest.supplierReferenceNumber && <Field label="Supplier Ref" value={purchaseRequest.supplierReferenceNumber} />}
            </div>

            <Separator className="mt-4" />

            {/* People */}
            <SectionHeading icon={Info} title="Raised by" />
            <div className="grid grid-cols-2 gap-x-6 gap-y-3">
              <Field label="Requested By" value={resolveName(purchaseRequest.requestedBy)} />
              {purchaseRequest.approvedBy && (
                <Field
                  label="Approved By"
                  value={`${resolveName(purchaseRequest.approvedBy)}${purchaseRequest.approvedDate ? ` · ${format(new Date(purchaseRequest.approvedDate), 'dd MMM yyyy')}` : ''}`}
                />
              )}
            </div>

            {/* FinOps deep-link */}
            <div className="pt-6">
              <Button variant="outline" className="w-full gap-2" asChild>
                <a
                  href={`#/financials/purchase-requests/${purchaseRequest.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ExternalLink className="h-4 w-4" />
                  View in Access Financials Operations
                </a>
              </Button>
            </div>
          </TabsContent>

          {/* ═══════════════════════════════════════════════════════════════
              TAB 2 — FINANCIAL JOURNEY
              Data sources:
              - PR status/stage:    FinOps GET /purchasing/transactions/purchase-requests/headers
              - PO / Order status:  Financials outstandingPurchaseOrders (filter by orderNumber)
              - GR status (proxy):  quantityDelivered on PO detail lines — no dedicated GR endpoint
              - Invoices:           Financials allTransactions (filter by orderNumber)
              - isCommitmentRelieved: computed field — see Engineering Notes UI-PTX-012
          ══════════════════════════════════════════════════════════════════ */}
          <TabsContent value="journey" className="flex-1 overflow-y-auto px-6 pb-6 mt-4">
            <div className="space-y-0">

              {/* Step 1 — PR Submitted */}
              <JourneyStep
                icon={FileText}
                label="Purchase Request"
                status={purchaseRequest.status}
                statusVariant={cfg.variant}
              >
                <p>Raised by {resolveName(purchaseRequest.requestedBy)} on {format(new Date(purchaseRequest.date), 'dd MMM yyyy')}</p>
                {purchaseRequest.category && <p>Category: {purchaseRequest.category}</p>}
              </JourneyStep>

              {/* Step 2 — Approval */}
              <JourneyStep
                icon={CheckCircle2}
                label="Approval"
                status={
                  isRejected ? 'Rejected / Cancelled' :
                  isInFlight ? 'Awaiting Approval' :
                  'Approved'
                }
                statusVariant={
                  isRejected ? 'destructive' :
                  isInFlight ? 'warning' :
                  'success'
                }
              >
                {purchaseRequest.approvedBy && (
                  <p>
                    Approved by {resolveName(purchaseRequest.approvedBy)}
                    {purchaseRequest.approvedDate && ` on ${format(new Date(purchaseRequest.approvedDate), 'dd MMM yyyy')}`}
                  </p>
                )}
                {isInFlight && <p>Approval is currently in progress in Access Financials Operations</p>}
                {isRejected && <p>This request was rejected or cancelled — no commitment was made</p>}
              </JourneyStep>

              {/* Step 3 — Purchase Order */}
              <JourneyStep
                icon={ShoppingCart}
                label="Purchase Order"
                status={
                  !hasOrder     ? 'Not Yet Raised' :
                  poStatus === 'Processed' ? 'Fully Processed' :
                  poStatus === 'Partially Processed' ? 'Partially Processed' :
                  'Raised'
                }
                statusVariant={
                  !hasOrder     ? 'outline' :
                  poStatus === 'Processed' ? 'success' :
                  poStatus === 'Partially Processed' ? 'warning' :
                  'default'
                }
              >
                {poNumbers.length > 0 && <p>PO Number(s): {poNumbers.join(', ')}</p>}
                {!hasOrder && !isRejected && (
                  <p className="italic">
                    {isInFlight
                      ? 'PO will be raised in Financials once approved'
                      : 'Awaiting posting to Financials'}
                  </p>
                )}
                {/* Engineering: netOutstanding from Financials outstandingPurchaseOrders.header
                    is the authoritative commitment value — wire here when API available */}
                {hasOrder && poStatus && <p>PO Status: {poStatus}</p>}
              </JourneyStep>

              {/* Step 4 — Goods / Services Received */}
              <JourneyStep
                icon={Package}
                label="Goods / Services Received"
                status={
                  !hasOrder       ? 'N/A' :
                  grStatus === 'Fully Received'    ? 'Fully Received' :
                  grStatus === 'Partially Received' ? 'Partially Received' :
                  grStatus === 'Not Received'      ? 'Not Received' :
                  grStatus ?? 'Awaiting'
                }
                statusVariant={
                  !hasOrder                         ? 'outline'     :
                  grStatus === 'Fully Received'     ? 'success'     :
                  grStatus === 'Partially Received' ? 'warning'     :
                  'outline'
                }
              >
                {grValue !== undefined && grValue > 0 && (
                  <p>Value received: {formatCurrency(grValue, purchaseRequest.currency)}</p>
                )}

              </JourneyStep>

              {/* Step 5 — Invoice */}
              <JourneyStep
                icon={Receipt}
                label="Invoice"
                status={
                  !hasOrder                         ? 'N/A' :
                  invoiceMatchedStatus === 'Fully Matched' ? 'Fully Matched' :
                  invoiceMatchedStatus === 'Partially Matched' ? 'Partially Matched' :
                  invoiceStatus ?? 'Not Invoiced'
                }
                statusVariant={
                  !hasOrder                                    ? 'outline'  :
                  invoiceMatchedStatus === 'Fully Matched'     ? 'success'  :
                  invoiceMatchedStatus === 'Partially Matched' ? 'warning'  :
                  invoiceStatus === 'Not Invoiced'             ? 'outline'  :
                  'default'
                }
              >
                {invoiceNumbers.length > 0 && (
                  <p>Invoice(s): {invoiceNumbers.join(', ')}</p>
                )}
                {hasOrder && !invoiceStatus && (
                  <p className="italic">
                    Invoice data from Financials
                    <code className="mx-1 text-[11px] bg-muted px-1 rounded">allTransactions</code>
                    — pending API integration (see Engineering Notes UI-PTX-012)
                  </p>
                )}
                {/* Engineering: transactionType from allTransactions distinguishes
                    Invoice / Credit Note / Payment — include all types here */}
              </JourneyStep>

              {/* Step 6 — Commitment Status */}
              <JourneyStep
                icon={isRelieved ? TrendingUp : CircleDashed}
                label="Commitment Status"
                status={
                  !hasOrder    ? 'No Commitment' :
                  isRelieved   ? 'Relieved — Paid' :
                  'Live Commitment'
                }
                statusVariant={
                  !hasOrder  ? 'outline'  :
                  isRelieved ? 'success'  :
                  'warning'
                }
                isLast
              >
                {isRelieved ? (
                  <p>PO has been fully invoiced and paid — commitment has been relieved in the finance system</p>
                ) : hasOrder ? (
                  <p>
                    This PO still carries an open commitment.{' '}
                    {/* Engineering: replace this with netOutstanding from
                        Financials outstandingPurchaseOrders.header */}
                    The outstanding value will reduce as invoices are matched and paid in Financial Operations.
                  </p>
                ) : (
                  <p>No purchase order has been raised — no commitment exists in the finance system</p>
                )}
                {!hasOrder && isRejected && (
                  <div className="flex items-center gap-1.5 mt-1">
                    <Ban className="h-3.5 w-3.5 text-error-500 shrink-0" />
                    <p>Request was rejected or cancelled</p>
                  </div>
                )}
              </JourneyStep>
            </div>

            {/* Engineering callout */}
            <div className="mt-4 rounded-md border border-warning-200 bg-warning-50 dark:bg-warning-950/20 dark:border-warning-800 px-4 py-3 text-xs text-warning-800 dark:text-warning-300 space-y-1">
              <p className="font-semibold flex items-center gap-1.5">
                <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                Engineering — API integration required for full journey data
              </p>
              <p>
                PO status, goods received quantities, and invoice data require the Financials
                GraphQL API (<code className="bg-warning-100 dark:bg-warning-900 px-1 rounded">outstandingPurchaseOrders</code> and{' '}
                <code className="bg-warning-100 dark:bg-warning-900 px-1 rounded">allTransactions</code>)
                to be wired into this panel. See <strong>Engineering Notes UI-PTX-012</strong> in the change log.
              </p>
            </div>
          </TabsContent>

          {/* ═══════════════════════════════════════════════════════════════
              TAB 3 — LINE ITEMS
              Source: FinOps GET /purchasing/transactions/purchase-requests/details
              Enrichment (pending API): quantityDelivered / quantityInvoiced
              from Financials outstandingPurchaseOrders.details
          ══════════════════════════════════════════════════════════════════ */}
          <TabsContent value="lines" className="flex-1 min-h-0 flex flex-col mt-0">
            {lineItemsLoading ? (
              <div className="flex flex-col items-center justify-center py-12 gap-3">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Loading line items…</p>
              </div>
            ) : lineItemsError ? (
              <div className="flex flex-col items-center justify-center py-12 gap-2 text-destructive">
                <AlertCircle className="h-5 w-5" />
                <p className="text-sm">{lineItemsError}</p>
              </div>
            ) : lineItems.length > 0 ? (
              <>
                {/* Row count bar */}
                <div className="flex items-center justify-between px-6 py-2 border-b bg-muted/30 shrink-0">
                  <p className="text-xs text-muted-foreground">
                    {lineItems.length} line{lineItems.length !== 1 ? 's' : ''}
                  </p>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span>Net: <span className="font-medium text-foreground">{formatCurrency(lineNet, purchaseRequest.currency)}</span></span>
                    {lineTax > 0 && (
                      <span>VAT: <span className="font-medium text-foreground">{formatCurrency(lineTax, purchaseRequest.currency)}</span></span>
                    )}
                    {lineTax > 0 && (
                      <span>Gross: <span className="font-semibold text-foreground">{formatCurrency(lineGross, purchaseRequest.currency)}</span></span>
                    )}
                  </div>
                </div>

                {/* Scrollable table */}
                <div className="flex-1 overflow-auto">
                  <Table>
                    <TableHeader className="sticky top-0 z-10 bg-card after:absolute after:bottom-0 after:left-0 after:right-0 after:h-px after:bg-border">
                      <TableRow>
                        <TableHead className="w-12 text-center">#</TableHead>
                        <TableHead className="w-20">Type</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead className="w-32">Cost Centre</TableHead>
                        <TableHead className="text-right w-16">Qty</TableHead>
                        <TableHead className="text-right w-28">Unit Price</TableHead>
                        <TableHead className="text-right w-28">Net</TableHead>
                        <TableHead className="w-16">VAT Code</TableHead>
                        <TableHead className="text-right w-28">VAT</TableHead>
                        <TableHead className="w-32">GL Account</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {lineItems.map((line) => (
                        <TableRow key={line.id}>
                          <TableCell className="text-center text-xs text-muted-foreground">{line.lineNumber}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">{line.lineType}</Badge>
                          </TableCell>
                          <TableCell>
                            <p className="text-sm">{line.description}</p>
                          </TableCell>
                          <TableCell className="text-sm">
                            {line.costCentreCode
                              ? <span>{line.costCentreCode}{line.costCentreName ? ` — ${line.costCentreName}` : ''}</span>
                              : <span className="text-muted-foreground">—</span>}
                          </TableCell>
                          <TableCell className="text-right text-sm">
                            {line.lineType === 'Text' ? '—' : line.quantity}
                          </TableCell>
                          <TableCell className="text-right text-sm">
                            {line.lineType === 'Text' || !line.unitPrice ? '—' : formatCurrency(line.unitPrice, line.currency)}
                          </TableCell>
                          <TableCell className="text-right text-sm font-medium">
                            {line.lineType === 'Text' ? '—' : formatCurrency(line.totalAmount, line.currency)}
                          </TableCell>
                          <TableCell>
                            {line.taxCode
                              ? <Badge variant="secondary" className="text-xs">{line.taxCode}</Badge>
                              : <span className="text-muted-foreground text-sm">—</span>}
                          </TableCell>
                          <TableCell className="text-right text-sm">
                            {line.lineType === 'Text' ? '—' : line.taxAmount !== undefined && line.taxAmount > 0
                              ? formatCurrency(line.taxAmount, line.currency)
                              : <span className="text-muted-foreground">—</span>}
                          </TableCell>
                          <TableCell>
                            {line.glAccount
                              ? <span className="font-mono text-xs">{line.glAccount}</span>
                              : <span className="text-muted-foreground text-sm">—</span>}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <FileText className="h-8 w-8 mb-2" />
                <p className="text-sm">No line items available</p>
              </div>
            )}
          </TabsContent>

          {/* ═══════════════════════════════════════════════════════════════
              TAB 4 — DOCUMENTS
              Placeholder — Document Management API not yet available.
              Engineering: integrate Document Management API or MFE.
              See Engineering Notes UI-PTX-012.
          ══════════════════════════════════════════════════════════════════ */}
          <TabsContent value="documents" className="flex-1 overflow-y-auto px-6 pb-6 mt-4">
            {purchaseRequest.documents && purchaseRequest.documents.length > 0 ? (
              <div className="space-y-2">
                {purchaseRequest.documents.map((doc) => {
                  const DocIcon = getDocumentIcon(doc.type);
                  return (
                    <div
                      key={doc.id}
                      className="flex items-center gap-3 p-3 rounded-lg border hover:bg-muted/40 transition-colors"
                    >
                      <DocIcon className="h-5 w-5 text-muted-foreground shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{doc.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatFileSize(doc.size)} · {format(new Date(doc.uploadDate), 'dd MMM yyyy')}
                        </p>
                      </div>
                      <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" asChild>
                        <a href={doc.url} download={doc.name}>
                          <Download className="h-4 w-4" />
                        </a>
                      </Button>
                    </div>
                  );
                })}
              </div>
            ) : (
              /* Placeholder state — will be replaced with Document Management MFE */
              <div className="flex flex-col items-center justify-center py-16 text-center gap-3">
                <div className="h-14 w-14 rounded-full bg-muted flex items-center justify-center">
                  <FolderOpen className="h-7 w-7 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-sm font-medium">Documents coming soon</p>
                  <p className="text-sm text-muted-foreground mt-1 max-w-xs">
                    Documents attached to this purchase request will appear here
                    once the Document Management integration is available.
                  </p>
                </div>
                <div className="mt-2 rounded-md border border-warning-200 bg-warning-50 dark:bg-warning-950/20 dark:border-warning-800 px-4 py-3 text-xs text-warning-800 dark:text-warning-300 max-w-sm text-left">
                  <p className="font-semibold flex items-center gap-1.5 mb-1">
                    <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                    Engineering — API integration required
                  </p>
                  <p>
                    Integrate the Document Management API or replace this tab with
                    the Documents MFE when available. See <strong>Engineering Notes UI-PTX-012</strong>.
                  </p>
                </div>
              </div>
            )}
          </TabsContent>

          {/* ═══════════════════════════════════════════════════════════════
              TAB 5 — APPROVAL HISTORY
              Source: FinOps approvalHistory[] on the PurchaseRequest.
              Engineering: no dedicated history endpoint exists — history is
              currently embedded in the PR record. A dedicated endpoint is
              required for large approval chains. See Engineering Notes UI-PTX-012.
          ══════════════════════════════════════════════════════════════════ */}
          <TabsContent value="history" className="flex-1 overflow-y-auto px-6 pb-6 mt-4">
            {purchaseRequest.approvalHistory && purchaseRequest.approvalHistory.length > 0 ? (
              <ApprovalHistoryTimeline
                approvalHistory={purchaseRequest.approvalHistory}
                requestId={purchaseRequest.requestId}
              />
            ) : (
              <div className="flex flex-col items-center justify-center py-16 text-center gap-3">
                <div className="h-14 w-14 rounded-full bg-muted flex items-center justify-center">
                  <History className="h-7 w-7 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-sm font-medium">No approval history available</p>
                  <p className="text-sm text-muted-foreground mt-1 max-w-xs">
                    Approval history for this purchase request is managed in
                    Access Financials Operations. A dedicated history endpoint
                    is required — see Engineering Notes UI-PTX-012.
                  </p>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}
